import { Box, IconButton, Modal, Typography } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import { Link as RouterLink } from "react-router";
import { decodeHtml } from "../lib/displayHelpers.js";

interface VideoLightboxProps {
  open: boolean;
  onClose: () => void;
  embedUrl: string | null;
  title: string;
  detailSlug: string;
}

function autoplaySrc(embedUrl: string): string {
  // Append autoplay=1 (and rel=0 for cleaner experience). YouTube embeds
  // already autoplay when opened in a user-initiated modal, but we set the
  // param explicitly so other providers behave too.
  const sep = embedUrl.includes("?") ? "&" : "?";
  return `${embedUrl}${sep}autoplay=1&rel=0`;
}

export function VideoLightbox({ open, onClose, embedUrl, title, detailSlug }: VideoLightboxProps) {
  const cleanTitle = decodeHtml(title);
  return (
    <Modal
      open={open}
      onClose={onClose}
      aria-labelledby="video-lightbox-title"
      sx={{ display: "flex", alignItems: "center", justifyContent: "center", p: { xs: 1, md: 4 } }}
    >
      <Box
        sx={{
          position: "relative",
          width: "100%",
          maxWidth: 1200,
          outline: "none",
        }}
      >
        {/* Top bar with title + actions */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            mb: 1.5,
            color: "common.white",
          }}
        >
          <Typography
            id="video-lightbox-title"
            variant="h6"
            sx={{
              flex: 1,
              fontWeight: 700,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {cleanTitle}
          </Typography>
          <IconButton
            component={RouterLink}
            to={`/content/${detailSlug}`}
            onClick={onClose}
            aria-label="Open full page"
            sx={{ color: "common.white", "&:hover": { color: "primary.main" } }}
          >
            <OpenInNewIcon />
          </IconButton>
          <IconButton
            onClick={onClose}
            aria-label="Close"
            sx={{ color: "common.white", "&:hover": { color: "primary.main" } }}
          >
            <CloseIcon />
          </IconButton>
        </Box>

        {/* 16:9 player. YouTube does its own letterboxing when source isn't
            16:9 — that's expected. */}
        <Box
          sx={{
            position: "relative",
            width: "100%",
            aspectRatio: "16 / 9",
            bgcolor: "common.black",
            borderRadius: 1,
            overflow: "hidden",
            boxShadow: "0 0 60px rgba(255,176,0,0.25)",
          }}
        >
          {embedUrl && (
            <Box
              component="iframe"
              src={autoplaySrc(embedUrl)}
              title={cleanTitle}
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
              sx={{ position: "absolute", inset: 0, width: "100%", height: "100%", border: 0 }}
            />
          )}
        </Box>
      </Box>
    </Modal>
  );
}
